import type { WeekRation, WeekRow } from '@/lib/weekRation';
import {
  W, SAFE_TOP, SAFE_BOTTOM, EMERALD, INK, INK_MUTED, INK_DIM, PAD_X,
  wrap, newCard, drawDofi, drawFooter, toPng,
} from '@/utils/cardCanvas';

// ════════════════════════════════════════════════════════════════
// Карточка «Моя неделя в дневнике» — сам разбор, а не значок достижения.
//
// Слава 07.10.2026: «было бы круто, если бы он не листик формировал, а именно
// этот разбор микронутриентов и Дофи. Это же маркетинг». До этого наружу уходила
// общая карточка достижения с 🌿 и цифрой 5.
//
// ⚠️ Правило 29.09.2026 в силе: делятся гордостью, а не дефицитом. На карточку
// попадают только сильные нутриенты (от 50% нормы) и найденные цвета радуги.
// Провалы недели и калорийность остаются в приложении.
// ════════════════════════════════════════════════════════════════

const MIN_PERCENT = 50;
const MAX_ROWS = 3;

function days(n: number): string {
  const ten = n % 100, one = n % 10;
  if (ten >= 11 && ten <= 14) return 'дней';
  if (one === 1) return 'день';
  if (one >= 2 && one <= 4) return 'дня';
  return 'дней';
}

function meals(n: number): string {
  const ten = n % 100, one = n % 10;
  if (ten >= 11 && ten <= 14) return 'приёмов';
  if (one === 1) return 'приём';
  if (one >= 2 && one <= 4) return 'приёма';
  return 'приёмов';
}

function colors(n: number): string {
  if (n === 1) return 'цвет';
  if (n >= 2 && n <= 4) return 'цвета';
  return 'цветов';
}

function plants(n: number): string {
  const ten = n % 100, one = n % 10;
  if (ten >= 11 && ten <= 14) return 'растений';
  if (one === 1) return 'растение';
  if (one >= 2 && one <= 4) return 'растения';
  return 'растений';
}

/** Фраза Дофи — по самому яркому, что есть в неделе. Без модели: мгновенно и бесплатно. */
export function weekDofiLine(w: WeekRation, rows: WeekRow[]): string {
  const top = rows[0];
  if (top && top.percent >= 100) return `${top.label} — ${top.percent}% нормы в среднем за день. Вот это неделя!`;
  if (w.rainbow.length >= 5) return `${w.rainbow.length} ${colors(w.rainbow.length)} на тарелке за неделю. Так и выглядит разнообразие.`;
  return `${w.streak} ${days(w.streak)} подряд в дневнике. Так и складывается привычка.`;
}

export function weekShareRows(w: WeekRation): WeekRow[] {
  return [...w.all].sort((a, b) => b.percent - a.percent).filter((r) => r.percent >= MIN_PERCENT).slice(0, MAX_ROWS);
}

export async function renderWeekCard(
  w: WeekRation,
  opts: { name?: string | null; refCode?: string | null } = {},
): Promise<Blob> {
  const { canvas, ctx } = await newCard();
  const rows = weekShareRows(w);

  await drawDofi(ctx, { cy: SAFE_TOP + 160, size: 290, pose: 'joy' });

  let y = SAFE_TOP + 375;
  ctx.textAlign = 'center';
  ctx.fillStyle = INK;
  ctx.font = '700 64px "Space Grotesk", Inter, system-ui, sans-serif';
  ctx.fillText('Моя неделя в дневнике', W / 2, y);
  y += 62;
  ctx.fillStyle = INK_MUTED;
  ctx.font = '500 34px Inter, system-ui, sans-serif';
  const sub = [opts.name, `${w.streak} ${days(w.streak)} подряд`, `${w.meals} ${meals(w.meals)}`].filter(Boolean).join(' · ');
  ctx.fillText(sub, W / 2, y);
  y += 100;

  // ── Чем неделя сильна: полоска до нормы, цифра в день ──
  if (rows.length) {
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(16,229,142,0.85)';
    ctx.font = '600 26px Inter, system-ui, sans-serif';
    ctx.letterSpacing = '4px';
    ctx.fillText('ЧЕМ НЕДЕЛЯ СИЛЬНА', PAD_X, y);
    ctx.letterSpacing = '0px';
    y += 70;
    const barW = W - PAD_X * 2;
    for (const r of rows) {
      ctx.textAlign = 'left';
      ctx.fillStyle = INK;
      ctx.font = '500 40px Inter, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
      ctx.fillText(`${r.emoji} ${r.label}`, PAD_X, y);
      ctx.textAlign = 'right';
      ctx.fillStyle = r.percent >= 100 ? EMERALD : INK;
      ctx.font = '700 40px "Space Grotesk", Inter, system-ui, sans-serif';
      ctx.fillText(`${r.percent}%`, W - PAD_X, y);
      const pctW = ctx.measureText(`${r.percent}%`).width;
      ctx.fillStyle = INK_DIM;
      ctx.font = '400 28px Inter, system-ui, sans-serif';
      ctx.fillText(`~${r.perDay} ${r.unit} в день  `, W - PAD_X - pctW, y);

      const by = y + 24;
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      roundRect(ctx, PAD_X, by, barW, 16, 8); ctx.fill();
      const fill = Math.max(16, barW * Math.min(1, r.percent / 100));
      const grad = ctx.createLinearGradient(PAD_X, 0, PAD_X + fill, 0);
      grad.addColorStop(0, 'rgba(16,229,142,0.55)');
      grad.addColorStop(1, EMERALD);
      ctx.fillStyle = grad;
      roundRect(ctx, PAD_X, by, fill, 16, 8); ctx.fill();
      y += 110;
    }
    y += 10;
  }

  // ── Радуга недели: только найденные цвета ──
  if (w.rainbow.length) {
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(16,229,142,0.85)';
    ctx.font = '600 26px Inter, system-ui, sans-serif';
    ctx.letterSpacing = '4px';
    ctx.fillText('РАДУГА НЕДЕЛИ', PAD_X, y);
    ctx.letterSpacing = '0px';
    y += 62;
    const r = 26, step = 74;
    for (let i = 0; i < w.rainbow.length; i++) {
      const cx = PAD_X + r + i * step;
      ctx.fillStyle = w.rainbow[i].dot;
      ctx.beginPath(); ctx.arc(cx, y, r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.textAlign = 'left';
    ctx.fillStyle = INK_MUTED;
    ctx.font = '400 30px Inter, system-ui, sans-serif';
    const tx = PAD_X + w.rainbow.length * step + 16;
    ctx.fillText(`${w.rainbow.length} ${colors(w.rainbow.length)} · ${w.plants} ${plants(w.plants)}`, tx, y + 11);
    y += 110;
  }

  // ── Фраза Дофи ──
  ctx.textAlign = 'center';
  ctx.fillStyle = INK_MUTED;
  ctx.font = 'italic 400 36px Georgia, "Times New Roman", serif';
  const lines = wrap(ctx, `«${weekDofiLine(w, rows)}»`, W - 240).slice(0, 2);
  for (const line of lines) { ctx.fillText(line, W / 2, y); y += 50; }
  ctx.fillStyle = INK_DIM;
  ctx.font = '400 26px Inter, system-ui, sans-serif';
  ctx.fillText('— Дофи', W / 2, y + 4);

  // Подвал прижимаем к низу безопасной зоны, но не выше содержимого
  const footerH = opts.refCode ? 160 : 116;
  drawFooter(ctx, Math.max(y + 70, SAFE_BOTTOM - footerH), opts.refCode);
  return toPng(canvas);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
